import { useEffect } from "react";
import styled from "styled-components";
import queryString from "query-string";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { CircularProgress, Typography } from "@mui/material";

import { useAxios } from "../../hooks";
import pages from "../../constants/pages";
import { colors } from "../css components/Style";
import { alert } from "../../redux/slices/alertSlice";
import { signInMember } from "../../redux/slices/memberSlice";
import LOGO from "../../assets/auth carousel pics/ic_logo.svg";
import { verifyRoleFromSignature } from "../../utils/encryptHash";

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  text-align: center;
  justify-content: center;
  background: #e5cce200;
  height: 100%;
  padding: 0 30px;
`;

export default function LandingPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const axios = useAxios({ disableAuthToken: true });

  const uiDetails = useSelector((state) => state.renderUIDetails);
  const token = queryString.parse(window.location.search)?.token;

  useEffect(() => {
    if (token) {
      sessionStorage.setItem("ida_sso_login", "true");

      axios({
        url: "/sso/login",
        method: "POST",
        data: {
          token: token,
          redirectionToken: true,
        },
      }).then((response) => {
        if (response.status) {
          getStateData(response);
        } else {
          // window.location = "https://ida-dev.gammasprint.com";

          dispatch(
            alert({
              type: "error",
              message: response.message,
            })
          );
        }
      });
    }
  }, [axios, token, dispatch]);

  return (
    <Wrapper>
      <Helmet title="Landing Page" />
      <div style={{ width: "600px" }}>
        <div className="landingPage-image-div">
          <img className="landingPage-image" src={LOGO} alt="logo" />
        </div>

        <Typography style={{ marginTop: "20px" }} variant="h4">
          You are being redirected.
        </Typography>

        <div className="landing-loader ">
          <svg width={0} height={0}>
            <defs>
              <linearGradient
                id="my_gradient"
                x1="0%"
                y1="0%"
                x2="0%"
                y2="100%"
              >
                <stop offset="0%" stopColor={colors.sideBarColor} />
                <stop offset="100%" stopColor={colors.sideBarSubColor} />
              </linearGradient>
            </defs>
          </svg>
          <CircularProgress
            sx={{ "svg circle": { stroke: "url(#my_gradient)" } }}
          />
        </div>
      </div>
    </Wrapper>
  );

  async function getStateData(response) {
    const data = response.data;

    const role = await verifyRoleFromSignature(
      response.data?.user[0]?.account_type,
      data.user[0]?.user_id
    );

    const timeStamp = new Date(data?.expiresIn).getTime();
    localStorage.setItem("expirySession", timeStamp);

    if (data.user[0]?.is_parent_brand_verified === 2) {
      dispatch(
        alert({
          type: "warning",
          message:
            "Your brand has been rejected by the admin. Please contact the admin for further details.",
        })
      );
      return;
    }
    if (role === "NORMAL_USER") {
      sessionStorage.setItem("ladder", JSON.stringify(data?.steps));
      sessionStorage.setItem("userID", parseInt(data.user[0]?.user_id));
      sessionStorage.setItem("authToken", data.token);
      sessionStorage.setItem(
        "businessEntityId",
        data.user[0]?.business_entity_id
      );

      sessionStorage.setItem("brandID", response.data.brands[0]?.brand_id);
      sessionStorage.setItem("role", data.user[0]?.role_name);

      if (!data.steps.step2 || !data.steps.step3) {
        navigate(pages.createWelcomePage.route);
      }

      if (data.steps.step2 && data.steps.step3) {
        if (data.user[0]?.is_parent_brand_verified === 0) {
          navigate(pages.createWelcomePage.route);
        } else if (!data.user[0]?.is_admin_verified) {
          navigate(pages.createWelcomePage.route);
        } else {
          if (data.brands[0].brand_type_id === 2) {
            sessionStorage.setItem("parentName", "Audience");
            sessionStorage.setItem("subParentName", "");

            localStorage.setItem(
              "dashboardState" + response.data.brands[0]?.brand_id,
              "monetization"
            );
          }

          if (data.brands[0].brand_type_id === 3) {
            sessionStorage.setItem("parentName", "Campaigns-request");
            sessionStorage.setItem("subParentName", "");
            localStorage.setItem(
              "dashboardState" + response.data.brands[0]?.brand_id,
              "promotion"
            );
          }

          sessionStorage.setItem(
            "activeBrand",
            response.data.brands[0]?.brand_id
          );
          sessionStorage.setItem("brandType", data.brands[0].brand_type_id);
          sessionStorage.setItem("role", "NORMAL_USER");

          await signInMember({
            authToken: data.token,
            userID: data.user[0]?.user_id,
            businessEntityId: data.user[0]?.business_entity_id,
            brandType: data.brands[0].brand_type_id,
          });

          if (data.brands[0].brand_type_id === 2) {
            navigate(pages.audienceList.route);
          }

          if (data.brands[0].brand_type_id === 3) {
            navigate(pages.campaignRequest.route);
          }
        }
      }
    }

    if (role === "AGENCY") {
      sessionStorage.setItem("ladder", JSON.stringify(data?.steps));
      sessionStorage.setItem("userID", parseInt(data.user[0]?.user_id));
      sessionStorage.setItem("authToken", data.token);
      sessionStorage.setItem(
        "businessEntityId",
        data.user[0]?.business_entity_id
      );

      sessionStorage.setItem("agencyID", data?.agency[0]?.agency_id);
      sessionStorage.setItem("role", data.user[0]?.role_name);

      if (!data.steps.step2 || !data.steps.step3) {
        navigate(pages.welcomeAsAgencyPage.route);
      }

      if (data.steps.step2 && data.steps.step3) {
        if (
          !data.user[0]?.is_admin_verified ||
          data.agency[0].is_agency_verified === 0
        ) {
          navigate(pages.welcomeAsAgencyPage.route);
        } else if (data.brands.length === 0) {
          navigate(pages.welcomePage.route);
        } else {
          navigate(pages.welcomePage.route);
        }
      }
    }

    if (role === "SUPER_ADMIN") {
      sessionStorage.setItem("role", data.user[0]?.role_name);

      await dispatch(
        signInMember({
          authToken: data.token,
          userID: parseInt(data.user[0]?.user_id),
          businessEntityId: parseInt(data.user[0]?.business_entity_id),
        })
      );
      navigate(pages.accountVerification.route);
    }

    //create new brand or company

    dispatch(
      alert({
        type: "success",
        message: response.message.displayMessage,
      })
    );
  }
}
